/**
 * @typedef {'json' | 'xml' | 'html' | 'chatbot'} OutputFormat
 */

/**
 * @typedef {Object} FAQItem
 * @property {string} question
 * @property {string} answer
 */

/**
 * @typedef {Object} FAQCategory
 * @property {string=} id
 * @property {string=} title
 * @property {FAQItem[]=} details
 * @property {FAQItem[]=} faqList
 */

import React, { useState, useCallback, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import {
  APP_MOBILE,
  APP_LANGUAGE,
  SUA_ENVIRONMENTS
} from '../../../utils/app_constants';

import faqContentService from '../../../services/faqContentService';
import config from '../../../configs/config';

import Fetcher from '../../../components/Fetcher';
import FAQMetaData from './FAQChatBotMetaData';
import FAQOutputRenderer from './FAQChatBotOutputRenderer';

import useLanguageDetection from './hooks/useLanguageDetection';
import useFAQAnalytics from './hooks/useFAQAnalytics';

import style from './KidsChatBotFAQ.module.css';

function KidsChatBotFAQ() {
  /** @type {[FAQCategory[], React.Dispatch<React.SetStateAction<FAQCategory[]>>]} */
  const [helpContent, setHelpContent] = useState([]);

  /** @type {[OutputFormat, React.Dispatch<React.SetStateAction<OutputFormat>>]} */
  const [rawFormat, setRawFormat] = useState('json');

  const location = useLocation();

  /** @type {string} */
  const language = useLanguageDetection();

  useFAQAnalytics(location, rawFormat);

  const currentEnvironment = config.apiSettings.environment;

  const formatParam = new URLSearchParams(location.search).get('format');

  /** Safe format synchronization */
  useEffect(() => {
    if (!formatParam) return;

    const lower = formatParam.toLowerCase();
    const validFormats = ['json', 'xml', 'html', 'chatbot'];

    if (validFormats.includes(lower)) {
      setRawFormat(/** @type {OutputFormat} */ (lower));
    }
  }, [formatParam]);

  /** Fully typed API callback */
  const actionCallback = useCallback(() => {
    if (!language) return false;

    /** @type {Promise<FAQCategory[]>} */
    const result = faqContentService.getFAQbyCategories({
      product: APP_MOBILE.WATCH,
      isProdEnv: currentEnvironment === SUA_ENVIRONMENTS.PRODUCTION,
      language: APP_LANGUAGE.ENGLISH
    });

    return result;
  }, [language, currentEnvironment]);

  return (
    <div className={style.container}>
      <div className={style.format_selector}>
        <h1>KidsWatch FAQ Data - ChatBot Format</h1>

        <p>
          Format: <strong>{rawFormat.toUpperCase()}</strong>
        </p>

        <p>
          Add <code>?format=json</code>, <code>?format=xml</code>, or{' '}
          <code>?format=html</code> to URL to change format.
        </p>
      </div>

      <Fetcher
        action={actionCallback}
        onLoad={setHelpContent}
        spinner={false}
        render={() =>
          helpContent.length === 0 ? (
            <div className={style.error}>
              <p>No FAQ data available</p>
            </div>
          ) : (
            <>
              <FAQMetaData helpContent={helpContent} language={language} />

              <FAQOutputRenderer
                data={helpContent}
                format={rawFormat}
                language={language}
              />
            </>
          )
        }
      />
    </div>
  );
}

export default KidsChatBotFAQ;
